/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import { createAuth0Client } from '@auth0/auth0-spa-js';
import mbpLogger from 'mbp-logger';
import { channel } from 'redux-saga';
import mbpUtil from 'mbp-api-util';
import { useAccessToken } from '@auth/mbp-next/app/react';
import getAuth0Config from './auth-config';

import {
    customCallbackURL,
    deffer,
    getAccessTokenInSession,
    getTokenLocalStorage,
    parseUserInfo,
    getGdprQuery,
} from './helpers';

import {
    logUserLoggedInFailed,
    logGuestSession,
    logHydraidUserSession,
    logUserLoggedOut,
} from '../../Auth-Actions';

import { getGuestUserAccessToken } from './guest';

const REACT_APP_INJECTABLE = mbpUtil.getEnv('REACT_APP_INJECTABLE');

export const authChannel = channel();
let brandName;
if ((typeof window !== 'undefined') && window.location && window.location.hostname) brandName = window.location.hostname.split('.')[1]; //  state is not available with brand value so couldn't use getbrandName hook here.
const authConfig = getAuth0Config(brandName);

const auth0Client = () => {
    const isSSR = typeof window === 'undefined';
    const waitAuthReady = deffer();

    let authInstance = null;
    let isReady = false;

    async function createClient(clientId, timeoutInSeconds) {
        if (!isSSR && !window.isSecureContext) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                fileName: 'auth.js',
                function: 'createClient',
                message: 'failed to create auth0 client, browser is running in an unsecure context',
            });
            return;
        }

        if (authInstance || !clientId) {
            return;
        }

        try {
            authInstance = await createAuth0Client({
                domain: authConfig.domain,
                clientId,
                authorizationParams: {
                    redirect_uri: `${authConfig.redirectURL}`,
                    appSource: 'PWA',
                    audience: authConfig.audience,
                },
                useRefreshTokens: true,
                cacheLocation: 'localstorage',
                useCookiesForTransactions: true,
                authorizeTimeoutInSeconds: timeoutInSeconds || 20,
                useRefreshTokensFallback: true,
            });
        } catch (ex) {
            mbpLogger.logError({
                appName: process.env.npm_package_name,
                module: 'mbp-pwa-ui',
                fileName: 'auth.js',
                function: 'createClient',
                jsError: ex,
                message: 'failed to create auth0 client, this might be due to browser version or timeout',
            });
        }
    }

    function getInstance() {
        return authInstance;
    }

    const setAuthReady = () => {
        isReady = true;

        waitAuthReady.resolve();
    };

    const getUserInfo = async () => {
        const user = await authInstance.getUser();

        const userInfo = parseUserInfo(
            user,
            ['firstName', 'lastName', 'contactId', 'email', 'XAtt3', 'XAtt4', 'XAtt5', 'loginsCount', 'loyaltyStatus'],
        );

        return userInfo;
    };

    const logout = async () => {
        if (typeof window !== 'undefined') {
            if (authInstance) {
                await authInstance.logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                    },
                });
            } else {
                window.location.href = '/';
            }
        }
    };

    return {
        isReady,
        waitAuthReady,
        createClient,
        getInstance,
        setAuthReady,
        getUserInfo,
        logout,
    };
};

export const auth0 = auth0Client();

export const loginWithInterstital = async (params = {}) => {
    const authClient = auth0.getInstance();

    const appState = {
        routeBack: params.routeBack,
    };

    const callbackURL = customCallbackURL(authConfig.redirectURL, {
        source: authConfig.appSource,
        ...params,
    });

    authClient.loginWithRedirect({
        appState,
        authorizationParams: {
            audience: authConfig.audience,
            callbackURL,
            ...params,
        },
    });
};

export const loginWithRedirect = async (options = {}, params = {}, brandCode) => {
    try {
        const authClient = auth0.getInstance();
        const auth0Options = options;
        const appState = {};
        const callbackURL = customCallbackURL(authConfig.redirectURL, {
            source: authConfig.appSource,
            brandName: brandCode,
            ...params,
        });

        auth0Options['ext-gdpr-value'] = getGdprQuery()?.gdprValue;
        auth0Options['ext-brand'] = brandCode;
        auth0Options['ext-last-visited-page'] = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;

        if (params?.routeBack) {
            appState.routeBack = params.routeBack;
        }

        if (params?.register === 'Y') auth0Options['screen_hint'] = 'signup';

        await authClient.loginWithRedirect({
            appState,
            authorizationParams: {
                audience: authConfig.audience,
                callbackURL,
                ...auth0Options,
            },
        });
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            fileName: 'auth.js',
            function: 'loginWithRedirect',
            jsError: ex,
            message: 'failed to call login with redirect',
        });
    }
};

const checkRegisteredAccessToken = async (accessTokenInSession, serverSideToken = '', profile = {}, standaloneApp = false) => {
    let token = '';

    try {
        if (standaloneApp) {
            token = accessTokenInSession;
            if (accessTokenInSession !== serverSideToken) {
                token = accessTokenInSession;
                authChannel.put(logHydraidUserSession({
                    user: {
                        ...profile,
                        token,
                    },
                    isAuthenticated: true,
                    accessToken: serverSideToken,
                }));
            }
        } else {
            const authClient = auth0.getInstance();

            if (authClient) {
                token = await authClient.getTokenSilently({
                    authorizationParams: {
                        audience: authConfig.audience,
                    },
                });

                if (accessTokenInSession !== token) {
                    const userInfo = await auth0.getUserInfo();

                    authChannel.put(logHydraidUserSession({
                        user: userInfo,
                        isAuthenticated: true,
                        accessToken: token,
                    }));
                }
            }
        }
    } catch (ex) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'mbp-pwa-ui',
            fileName: 'auth.js',
            function: 'checkRegisteredAccessToken',
            jsError: ex,
            message: 'failed to silent auth',
        });

        authChannel.put(logUserLoggedInFailed());

        authChannel.put(logUserLoggedOut());

        auth0.logout();
    }

    return token;
};

const checkGuestAccessToken = async (accessTokenInSession, serverSideToken = '', standaloneApp = false) => {
    if (standaloneApp) {
        if (accessTokenInSession !== serverSideToken) {
            let userInfo = getTokenLocalStorage('userInfo') || null;
            userInfo = JSON.parse(userInfo);

            authChannel.put(logGuestSession({
                user: userInfo?.profile || {},
                isAuthenticated: false,
                accessToken: serverSideToken,
            }));
        }
        return serverSideToken;
    }
    const token = await getGuestUserAccessToken();

    if (accessTokenInSession !== token) {
        let userInfo = getTokenLocalStorage('userInfo') || null;
        userInfo = JSON.parse(userInfo);

        authChannel.put(logGuestSession({
            user: userInfo?.profile || {},
            isAuthenticated: false,
            accessToken: token,
        }));
    }
    return token;
};

let getAccessTokenSafely = async () => {
    let accessToken = '';
    if (REACT_APP_INJECTABLE === 'false') {
        // useAccessToken will pick value from localstorage if exist else call to api to fetch
        let accessTokenInSession = getAccessTokenInSession();
        let serverSideToken = await useAccessToken();
        const jwtTest = mbpUtil.testJWT(serverSideToken.accessToken || serverSideToken.token);
        if ((!jwtTest?.isValid && accessToken?.type === 'R') || (jwtTest?.isExpired && accessToken?.type === 'R')) {
            serverSideToken = await useAccessToken({ storagePolicy: 'network-first' });
        }
        /** Flag is only to use put the value in redux store on initial load */
        if (!window?.refreshAuth && (!jwtTest?.isValid || jwtTest?.isExpired) && accessToken?.type === 'R') {
            accessTokenInSession = '';
            window.refreshAuth = true; // set true after first load
        }
        if ((jwtTest?.isValid && !jwtTest?.isGuest) || serverSideToken?.type === 'R') { // registered user
            accessToken = await checkRegisteredAccessToken(accessTokenInSession, serverSideToken?.accessToken, serverSideToken?.user || serverSideToken?.profile, true);
        } else {
            accessToken = await checkGuestAccessToken(accessToken.accessToken, serverSideToken?.accessToken, true);
        }
        return accessToken;
    }

    if (!auth0.isReady) {
        await auth0.waitAuthReady;
    }

    const authClient = auth0.getInstance();

    if (!authClient) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'auth.js',
            message: `authClient was marked ready and is still ${authClient}`,
            function: 'getAccessTokenSafely',
        });

        // allow the function to finish to get the token in localstorage
    }

    try {
        // if no auth client, dont get registered and throw in the guest
        const isAuthenticated = authClient ? await authClient.isAuthenticated() : false;
        const accessTokenInSession = getAccessTokenInSession();
        const jwtTest = mbpUtil.testJWT(accessTokenInSession);

        if ((jwtTest?.isValid && !jwtTest?.isGuest) || isAuthenticated) { // registered user
            accessToken = await checkRegisteredAccessToken(accessTokenInSession);
        } else if (jwtTest?.isValid && jwtTest?.isGuest) { // guest user
            accessToken = await checkGuestAccessToken(accessTokenInSession);
        } else {
            accessToken = await checkGuestAccessToken(accessTokenInSession);
        }
    } catch (err) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            module: 'auth.js',
            function: 'getAccessTokenSafely',
            message: `Error checking the jwt token ${err.message}`,
            error: err,
        });
    }

    return accessToken;
};

// Use getAccessTokenSafely set in mbp-pwa-ui
if (typeof window !== 'undefined' && window.mbpAuth && window.mbpAuth.getAccessTokenSafely) {
    getAccessTokenSafely = window.mbpAuth.getAccessTokenSafely;
}

export {
    getAccessTokenSafely,
};
